import React, { useState, useEffect, useCallback } from "react";
import {
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Col,
  Button,
  CardBody,
  CardHeader,
} from "reactstrap";
import DatePicker from "react-datepicker";
import CreatableSelect from "react-select/creatable";
import { useDispatch, useSelector } from "react-redux";
import { showSweetAlert } from "../../Common/SweetAlert";
import {
  addWorkExpRequest,
  deleteWorkExpRequest,
  resetData,
} from "../../../redux/jobseeker/work_experience/actions";
import { fetchCompanyRequest } from "../../../redux/company/actions";
import { showToast } from "../../Common/Toast";
import { fetchJobseekerDataRequest } from "../../../redux/jobseeker/fetch_Jobseeker_data/actions";
import { FiPlusSquare } from "react-icons/fi";
import { convertNumberToWords, handleNumberInput, countWords } from "../../Common/CommonFunction";
import AddWorkexperienceCard from "./AddWorkexperienceCard";

const WorkExperienceForm = ({ profileData, validationMessages }) => {
  const dispatch = useDispatch();
  const [companyOptions, setCompanyOptions] = useState([]);
  const [errors, setErrors] = useState({}); // Initialize as an object

  const [formData, setFormData] = useState(() => {
    const workExperience = Array.isArray(profileData?.work_experience)
      ? profileData.work_experience
      : [];

    return (
      workExperience.map((exp) => ({
        work_exp_id: exp.id || "",
        designation: exp.designation || "",
        organization: exp.organization || null,
        job_description: exp.job_description || "",
        current_company: exp.current_company || "",
        notice_period: exp.notice_period || "",
        started_working_from: exp.started_working_from
          ? new Date(exp.started_working_from)
          : null,
        worked_till: exp.worked_till ? new Date(exp.worked_till) : null,
        current_salary: exp.current_salary || "",
      })) || [
        {
          work_exp_id: "",
          designation: "",
          organization: null,
          job_description: "",
          current_company: "",
          notice_period: "",
          started_working_from: null,
          worked_till: null,
          current_salary: "",
        },
      ]
    );
  });

  const [showNewEntryForm, setShowNewEntryForm] = useState(false);

  const companyReducerData = useSelector((state) => state.companyReducer);
  const companyData = companyReducerData.data.data;

  useEffect(() => {
    if (companyData && companyData.length > 0) {
      setCompanyOptions(companyData);
    }
  }, [companyData]);

  const handleChange = (e, index) => {
    const { name, value, type, checked } = e.target;
    const updatedFormData = [...formData];

    // Handle checkbox or radio button for "current_company"
    if (name === "current_company") {
      if (type === "checkbox") {
        updatedFormData[index][name] = checked ? "YES" : "NO"; // Check if the box is checked
        if (checked) {
          updatedFormData[index].worked_till = new Date(); // Set worked_till to the current date when checked
        } else {
          updatedFormData[index].worked_till = null; // Reset worked_till if unchecked
        }
      } else {
        updatedFormData[index][name] = value; // Handle other input types (e.g., text or radio)
      }
    } else {
      updatedFormData[index][name] = value; // Update normal fields
    }

    setFormData(updatedFormData);

    // Clear the error message for the updated field
    setErrors((prev) => {
      const updatedErrors = { ...prev }; // Use object spread
      if (!updatedErrors[index]) updatedErrors[index] = {};
      updatedErrors[index][name] = ""; // Clear the specific error
      return updatedErrors;
    });
  };

  const handleDateChange = (date, index, field) => {
    const updatedFormData = [...formData];
    updatedFormData[index][field] = date; // Update the specific date field
    setFormData(updatedFormData); // Update the state with the new form data

    // Clear the error message for the updated field
    setErrors((prev) => {
      const updatedErrors = { ...prev }; // Use object spread
      if (!updatedErrors[index]) updatedErrors[index] = {};
      updatedErrors[index][field] = ""; // Clear the specific error

      // Validation logic for Started Working From date
      if (field === "started_working_from") {
        const currentDate = new Date();
        const workedTillDate = updatedFormData[index].worked_till;

        if (updatedFormData[index].current_company === "YES") {
          if (date && date > currentDate) {
            updatedErrors[index][field] = "Started Working From date cannot be greater than current date.";
          }
        } else if (updatedFormData[index].current_company === "NO") {
          if (date && workedTillDate && date > workedTillDate) {
            updatedErrors[index][field] = "Started Working From date cannot be greater than Worked Till date.";
          }
        }
      }

      // Validation logic for Worked Till date
      if (field === "worked_till") {
        const startedFromDate = updatedFormData[index].started_working_from;

        if (date && startedFromDate && date < startedFromDate) {
          updatedErrors[index]["started_working_from"] = "Started Working From date cannot be greater than Worked Till date.";
        }
      }

      return updatedErrors; // Return the updated errors state
    });
  };

  const handleCompanyFocus = () => {
    dispatch(fetchCompanyRequest(""));
  };

  const handleCompanyInputChange = useCallback((inputValue) => {
    dispatch(fetchCompanyRequest(inputValue));
  }, []);

  const handleCompanyChange = (selectedOption, index) => {
    const updatedFormData = [...formData];
    updatedFormData[index].organization = selectedOption;
    setFormData(updatedFormData);
    setErrors((prev) => {
      const updatedErrors = { ...prev }; // Use object spread
      if (!updatedErrors[index]) updatedErrors[index] = {};
      updatedErrors[index].organization = ""; // Clear the specific error
      return updatedErrors;
    });
  };

  const validateForm = (index) => {
    const entry = formData[index];
    const entryErrors = {};
    if (!entry.designation) entryErrors.designation = "Designation is required.";
    if (!entry.organization) entryErrors.organization = "Organization is required.";
    if (!entry.current_company) entryErrors.current_company = "Please select if this is your current company.";
    if (!entry.started_working_from) {
      entryErrors.started_working_from = "Work from is required";
    } else {
      const currentDate = new Date();
      if (entry.started_working_from > currentDate) {
        entryErrors.started_working_from = "Started Working From date cannot be greater than current date.";
      }
      if (entry.current_company === "NO" && entry.worked_till) {
        if (entry.started_working_from > entry.worked_till) {
          entryErrors.started_working_from = "Started Working From date cannot be greater than Worked Till date.";
        }
      }
      const dobDate = new Date(profileData.jobseeker_date_of_birth); // e.g., "1993-07-28"
      const startedWorkingFrom = new Date(entry.started_working_from);
      // ✅ Validate against DOB
      if (startedWorkingFrom < dobDate) {
        entryErrors.started_working_from = "Started Working From date cannot be before Date of Birth.";
      }

    }
    if (entry.current_company === "NO" && !entry.worked_till) {
      entryErrors.worked_till = "Worked till date is required.";
    }
    if (entry.current_company === "YES" && !entry.notice_period) {
      entryErrors.notice_period = "Notice period is required.";
    }
    if (!entry.current_salary) entryErrors.current_salary = "Current salary is required.";
    if (!entry.job_description) {
      entryErrors.job_description = "Job description is required.";

    } else {

      const onlyNumbersDescriptionRegex = /^\d+$/; // Only numbers

      if (onlyNumbersDescriptionRegex.test(entry.job_description)) {
        entryErrors.job_description = "Job description cannot contain only numbers.";

      }

      // Minimum length check (50 characters)
      if (entry.job_description.length < 15) {
        errors.job_description = "Job description must be at least 15 characters.";

      }
    }

    return entryErrors; // Return errors specific to this entry
  };

  const handleSubmit = (index) => {
    const validationErrors = validateForm(index);
    if (Object.keys(validationErrors).length > 0) {
      const updatedErrors = { ...errors }; // Use object spread
      updatedErrors[index] = validationErrors; // Set errors for the specific entry
      setErrors(updatedErrors);
      return;
    }

    const entry = formData[index];
    const formattedData = {
      work_exp_id: entry.work_exp_id || "",
      designation: entry.designation || "",
      organization_id: entry.organization.value || "",
      organization: entry.organization.label || "",
      is_new: entry.organization.__isNew__ || false,
      job_description: entry.job_description || "",
      current_company: entry.current_company || "",
      notice_period: entry.current_company === "YES" && entry.notice_period ? entry.notice_period : "",
      started_working_from: entry.started_working_from || "",
      worked_till: entry.worked_till || "",
      current_salary: entry.current_salary || "",
    };

    dispatch(addWorkExpRequest(formattedData));
  };

  const handleWorkExperienceDeleteClick = (job_id) => {
    showSweetAlert("confirm", {
      message: "You want to delete the experience",
      fun: handleWorkExperience,
      id: job_id,
    });
  };

  const handleWorkExperience = (id) => {
    dispatch(deleteWorkExpRequest(id));
    setFormData((prevFormData) =>
      prevFormData.filter((exp) => exp.work_exp_id !== id)
    );
  };

  const addWorkExpReducerData = useSelector((state) => state.addWorkExpReducer);
  useEffect(() => {
    if (addWorkExpReducerData.status && addWorkExpReducerData.message) {
      showToast("success", addWorkExpReducerData.message);
      dispatch(resetData());
      dispatch(fetchJobseekerDataRequest());
    } else if (
      addWorkExpReducerData.status === false &&
      addWorkExpReducerData.message
    ) {
      showToast("error", addWorkExpReducerData.message);
      dispatch(resetData());
    }
  }, [addWorkExpReducerData]);

  const [showValidationMessages, setValidationMessages] = useState();
  useEffect(() => {
    // Check if validationMessages is an object with the errors
    if (validationMessages && typeof validationMessages === "object") {
      setValidationMessages(validationMessages);
      console.log("validationMessages888", validationMessages);
    }
  }, [validationMessages]);

  return (
    <Form>
      <CardHeader className="d-flex align-items-center justify-content-between">
        <h4>Work Experience</h4>
        <FiPlusSquare
          className="add-icon"
          size={22}
          onClick={() => setShowNewEntryForm(true)} // Show new entry form
          title="Add"
        />
      </CardHeader>
      <CardBody>
        {showValidationMessages && (
          <div className="text-danger mb-4">
            {showValidationMessages.work_experience}
          </div>
        )}
        {showNewEntryForm && (
          <AddWorkexperienceCard dob={profileData.jobseeker_date_of_birth} onClose={() => setShowNewEntryForm(false)} />
        )}
        {formData.map((exp, index) => (
          <div key={index} className="mb-4">
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    Designation <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    name="designation"
                    placeholder="Enter your designation"
                    className="form-control form-control-sm"
                    value={exp.designation}
                    maxLength={50}
                    onChange={(e) => {
                      const { value } = e.target;

                      // Validates if the input value contains only alphabetic characters (and spaces)
                      if (/^[A-Za-z\s]*$/.test(value)) {
                        handleChange(e, index); // Call your existing handleChange function with the index
                      } else {
                        // Optionally, set an error message or provide feedback
                        const updatedErrors = {
                          ...errors,
                          [index]: {
                            ...errors[index],
                            designation: "Only alphabetic characters are allowed.",
                          },
                        };
                        setErrors(updatedErrors);
                      }
                    }}
                  />
                  {errors[index]?.designation && (
                    <div className="text-danger">
                      {errors[index].designation}
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    Organization <span className="text-danger">*</span>
                  </Label>
                  <CreatableSelect
                    isClearable
                    options={companyOptions}
                    isLoading={companyReducerData.isLoading}
                    placeholder="Select Company"
                    value={exp.organization}
                    onChange={(option) => handleCompanyChange(option, index)}
                    onInputChange={handleCompanyInputChange}
                    onFocus={handleCompanyFocus}
                  />
                  {errors[index]?.organization && (
                    <div className="text-danger">
                      {errors[index].organization}
                    </div>
                  )}
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>
                    Is this your current company?{" "}
                    <span className="text-danger">*</span>
                  </Label>
                  <FormGroup>
                    <Label check>
                      <Input
                        checked={exp.current_company === "YES"}
                        type="radio"
                        name="current_company"
                        value="YES"
                        onChange={(e) => handleChange(e, index)}
                      />{" "}
                      Yes
                    </Label>
                    <Label check className="ms-3">
                      <Input
                        type="radio"
                        name="current_company"
                        value="NO"
                        onChange={(e) => handleChange(e, index)}
                        checked={exp.current_company === "NO"}
                      />{" "}
                      No
                    </Label>
                  </FormGroup>
                  {errors[index]?.current_company && (
                    <div className="text-danger">
                      {errors[index].current_company}
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    Started Working From <span className="text-danger">*</span>
                  </Label>
                  <DatePicker
                    showYearDropdown
                    showMonthDropdown
                    selected={exp.started_working_from}
                    onChange={(date) =>
                      handleDateChange(date, index, "started_working_from")
                    }
                    className="form-control form-control-sm mb-0"
                    dateFormat="dd/MM/yyyy"
                    dropdownMode="select"
                  />
                  {errors[index]?.started_working_from && (
                    <div className="text-danger">
                      {errors[index].started_working_from}
                    </div>
                  )}
                </FormGroup>
              </Col>

              <Col md={6} className={exp.current_company !== "NO" ? "d-none" : ""}>
                <FormGroup>
                  <Label>
                    Worked Till <span className="text-danger">*</span>
                  </Label>
                  <DatePicker
                    showYearDropdown
                    showMonthDropdown
                    className="form-control form-control-sm mb-0"
                    selected={exp.worked_till}
                    onChange={(date) =>
                      handleDateChange(date, index, "worked_till")
                    }
                    dateFormat="dd-MM-yyyy"
                    placeholderText="Enter work till"
                    disabled={exp.current_company === "YES"}
                    dropdownMode="select"
                  />
                  {errors[index]?.worked_till && (
                    <div className="text-danger">
                      {errors[index].worked_till}
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                {exp.current_company === "YES" && (
                  <FormGroup>
                    <Label>
                      Notice Period (In Days){" "}
                      <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="notice_period"
                      placeholder="Enter notice period"
                      value={exp.notice_period}
                      // onChange={(e) => handleChange(e, index)}

                      onInput={(e) => handleNumberInput(e, (event) => handleChange(event, index), 0, 365)}
                    />
                    {errors[index]?.notice_period && (
                      <div className="text-danger">
                        {errors[index].notice_period}
                      </div>
                    )}
                  </FormGroup>
                )}
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>
                    CTC<span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    name="current_salary"
                    placeholder="Enter your current salary"
                    value={exp.current_salary}
                    onInput={(e) => handleNumberInput(e, (event) => handleChange(event, index), 0, 990000000)}
                  />
                  {exp.current_salary && !isNaN(exp.current_salary) && (
                    <small className="text-muted text-capitalize">
                      {convertNumberToWords(exp.current_salary)}
                    </small>
                  )}
                  {errors[index]?.current_salary && (
                    <div className="text-danger">
                      {errors[index].current_salary}
                    </div>
                  )}
                </FormGroup>
              </Col>

              <Col md={12}>
                <FormGroup>
                  <Label>
                    Describe Your Job Profile
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="textarea"
                    name="job_description"
                    placeholder="Enter job description"
                    value={exp.job_description}
                    onChange={(e) => handleChange(e, index)}
                    maxLength={250}
                    rows="5"
                  />
                  <small className="text-muted">
                    Words: {countWords(formData.job_description)}
                  </small>
                  {errors[index]?.job_description && (
                    <div className="text-danger">
                      {errors[index].job_description}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col className="d-flex justify-content-end">
                <Button
                  className="me-2"
                  color="primary"
                  onClick={() => handleSubmit(index)}
                >
                  Save
                </Button>
                <Button
                  color="danger"
                  onClick={() =>
                    handleWorkExperienceDeleteClick({ id: exp.work_exp_id })
                  }
                >
                  Delete
                </Button>
              </Col>
            </Row>
          </div>
        ))}

        {errors.work_experience && (
          <small className="text-danger">{errors.work_experience}</small>
        )}
      </CardBody>
    </Form>
  );
};

export default WorkExperienceForm;